import type { DiagramEdge, DiagramNode, DiagramPage } from '../../core/types';
import { createEmptyDocument } from '../../core/defaults';
import { validateDocument } from '../../core/validation';

export interface CustomTemplatePreset {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

const KEY = 'owdex-draw:custom-templates:v1';
const LIMIT = 40;

function safeTemplate(value: unknown): CustomTemplatePreset | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Partial<CustomTemplatePreset>;
  if (typeof row.id !== 'string' || typeof row.name !== 'string' || typeof row.description !== 'string' || typeof row.createdAt !== 'string') return null;
  if (!Array.isArray(row.nodes) || !Array.isArray(row.edges)) return null;
  if (!row.nodes.every((node) => node && typeof node === 'object' && typeof (node as DiagramNode).id === 'string' && typeof (node as DiagramNode).type === 'string')) return null;
  if (!row.edges.every((edge) => edge && typeof edge === 'object' && typeof (edge as DiagramEdge).id === 'string' && typeof (edge as DiagramEdge).sourceId === 'string' && typeof (edge as DiagramEdge).targetId === 'string')) return null;
  return row as CustomTemplatePreset;
}

export function loadCustomTemplates(): CustomTemplatePreset[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || '[]') as unknown;
    return Array.isArray(parsed) ? parsed.flatMap((item) => safeTemplate(item) ? [safeTemplate(item)!] : []).slice(0, LIMIT) : [];
  } catch { return []; }
}

function persist(items: CustomTemplatePreset[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items.slice(0, LIMIT))); } catch { /* best-effort local-first */ }
}

export function saveCustomTemplate(name: string, description: string, page: DiagramPage): CustomTemplatePreset[] {
  const nodeIds = new Set(page.nodes.map((node) => node.id));
  const preset: CustomTemplatePreset = {
    id: crypto.randomUUID(),
    name: name.trim().slice(0, 100) || page.name || 'Modelo personalizado',
    description: description.trim().slice(0, 240),
    createdAt: new Date().toISOString(),
    nodes: structuredClone(page.nodes).map((node) => ({ ...node, locked: false, hidden: false, groupId: node.groupId ?? null })),
    edges: structuredClone(page.edges).filter((edge) => nodeIds.has(edge.sourceId) && nodeIds.has(edge.targetId)),
  };
  const next = [preset, ...loadCustomTemplates()]; persist(next); return next;
}

export function deleteCustomTemplate(id: string): CustomTemplatePreset[] {
  const next = loadCustomTemplates().filter((item) => item.id !== id); persist(next); return next;
}

export function exportCustomTemplatesJson(): string {
  return JSON.stringify({ format: 'owdex-draw-custom-templates', version: 1, templates: loadCustomTemplates() }, null, 2);
}

export function importCustomTemplatesJson(text: string): CustomTemplatePreset[] {
  const parsed = JSON.parse(text) as unknown;
  if (!parsed || typeof parsed !== 'object') throw new Error('Arquivo de modelos inválido.');
  const root = parsed as Record<string, unknown>;
  if (root.format !== 'owdex-draw-custom-templates' || root.version !== 1 || !Array.isArray(root.templates)) throw new Error('Formato de modelos não reconhecido.');
  const templates = root.templates.flatMap((item) => {
    const valid = safeTemplate(item); if (!valid) return [];
    const candidate = createEmptyDocument(); candidate.pages[0].nodes = structuredClone(valid.nodes); candidate.pages[0].edges = structuredClone(valid.edges);
    if (!validateDocument(candidate).valid) return [];
    return [{ ...structuredClone(valid), id: crypto.randomUUID(), name: valid.name.slice(0, 100), description: valid.description.slice(0, 240), createdAt: new Date().toISOString() } satisfies CustomTemplatePreset];
  }).slice(0, LIMIT);
  if (templates.length === 0 && root.templates.length > 0) throw new Error('Nenhum modelo válido foi encontrado.');
  persist(templates); return templates;
}

export function instantiateCustomTemplate(template: CustomTemplatePreset, targetX: number, targetY: number): { nodes: DiagramNode[]; edges: DiagramEdge[] } {
  const bounds = template.nodes.reduce((acc, node) => ({ minX: Math.min(acc.minX, node.x), minY: Math.min(acc.minY, node.y), maxX: Math.max(acc.maxX, node.x + node.width), maxY: Math.max(acc.maxY, node.y + node.height) }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
  const centerX = Number.isFinite(bounds.minX) ? (bounds.minX + bounds.maxX) / 2 : 0;
  const centerY = Number.isFinite(bounds.minY) ? (bounds.minY + bounds.maxY) / 2 : 0;
  const idMap = new Map<string, string>();
  const groupMap = new Map<string, string>();
  for (const node of template.nodes) idMap.set(node.id, crypto.randomUUID());
  const nodes = template.nodes.map((node) => {
    let groupId: string | null = null;
    if (node.groupId) { if (!groupMap.has(node.groupId)) groupMap.set(node.groupId, crypto.randomUUID()); groupId = groupMap.get(node.groupId)!; }
    return { ...structuredClone(node), id: idMap.get(node.id)!, x: Math.round(node.x - centerX + targetX), y: Math.round(node.y - centerY + targetY), locked: false, hidden: false, groupId };
  });
  const edges = template.edges.flatMap((edge) => {
    const sourceId = idMap.get(edge.sourceId), targetId = idMap.get(edge.targetId); if (!sourceId || !targetId) return [];
    return [{ ...structuredClone(edge), id: crypto.randomUUID(), sourceId, targetId }];
  });
  return { nodes, edges };
}
